// Phase-3 Slice 27 + 36 — steps 3 + 4 of the rename plan: rewrite each
// REFERENCING object's body, and surface the ones we couldn't.
//
// Extracted from `renamePlanner.ts`, which stays the facade every caller
// imports. Per object group (one body): fetch the body, narrow every surviving
// reference to its terminal token, rewrite via `emitSource`, and wrap the
// rewritten body in the dialect-native ALTER through the per-engine emitter.
// Objects we couldn't rewrite (parse-failed body, missing body, no per-dialect
// emitter, or an unsupported emit op — e.g. SQLite constraint rewrite) come back
// under `unrewritable[]` for the preview UI. Which references reach here, and
// with what confidence, is decided one step earlier in
// `renameReferenceCollection.ts`.

import type { DatabaseEngineId } from '@/core/engines';

import type { SqlEngine } from '../../sql-grammar/model';
import { emitSource } from '../../sql-resolver/edit/emitSource';
import type { PlannedByteEdit } from '../../sql-resolver/types';
import type { FetchObjectBodyOptions } from '../index/objectBodyFetcher';
import type { MatchConfidence } from '../relationIdentity';
import type {
  AlterStatement,
  ObjectBodyEdit,
  ReferenceRecord,
  RenameDestination,
  UnrewritableObject,
} from '../types';

import type { DialectRenameEmitter } from './emit';
import type { RefGroup } from './refGrouper';
import { dedupeByRange, weakestConfidence } from './renameReferenceCollection';
import { buildLineIndex, planTerminalTokenEdit } from './terminalTokenEdit';

export type PlanObjectBodyEditsInput = {
  readonly connectionId: string;
  readonly database: string;
  readonly engineId: DatabaseEngineId;
  readonly sqlEngine: SqlEngine;
  /** The renamed identifier's own leaf name — the only thing a body rewrite
   *  needs from the request beyond the destination below. */
  readonly fromName: string;
  /** The destination the plan settled once (`renameDestination.ts`). A body
   *  rewrite needs both halves of it: `name` (the DECODED destination, any
   *  delimiters the user typed already off) to decide whether a bare token
   *  still folds to the right column, and `catalogName` — what the schema ALTER
   *  actually creates — as the identity any delimited token must spell. */
  readonly destination: RenameDestination;
  /** `null` when the engine has no per-dialect emitter: every group then lands
   *  in `unrewritable` with reason `no-emitter`. */
  readonly emitter: DialectRenameEmitter | null;
  readonly groups: readonly RefGroup[];
  readonly confidenceByRef: ReadonlyMap<ReferenceRecord, MatchConfidence>;
  /** #34 opt-in: when true, ambiguous rewrites are INCLUDED in `objectEdits`
   *  (and therefore apply). */
  readonly includeAmbiguous: boolean;
  readonly fetchBody: (options: FetchObjectBodyOptions) => Promise<string | null>;
  /** Procedural bodies (procedure / trigger) come from this sibling feed —
   *  `fetchBody` hard-returns null for procedural kinds. */
  readonly fetchProceduralBody: (options: FetchObjectBodyOptions) => Promise<string | null>;
};

export type ObjectBodyEditsPlan = {
  readonly objectEdits: readonly ObjectBodyEdit[];
  readonly unrewritable: readonly UnrewritableObject[];
  /** The per-object undo statements, in the order their forward edits were
   *  planned. The planner prepends the schema-side undo to these. */
  readonly undoBody: readonly AlterStatement[];
  readonly ambiguousMatchCount: number;
};

export async function planObjectBodyEdits(
  input: PlanObjectBodyEditsInput,
): Promise<ObjectBodyEditsPlan> {
  const {
    connectionId,
    database,
    engineId,
    sqlEngine,
    fromName,
    destination,
    emitter,
    groups,
    confidenceByRef,
    includeAmbiguous,
    fetchBody,
    fetchProceduralBody,
  } = input;

  const objectEdits: ObjectBodyEdit[] = [];
  const unrewritable: UnrewritableObject[] = [];
  const undoBody: AlterStatement[] = [];
  let ambiguousMatchCount = 0;

  for (const group of groups) {
    if (group.references.some((r) => r.parseStatus === 'parse-failed')) {
      unrewritable.push({
        objectKind: group.objectKind,
        objectName: group.objectName,
        reason: 'parse-failed',
      });
      continue;
    }
    // #34: split this body's matched refs by confidence. Ambiguous refs
    // (unqualified against a qualified target, or 4-part linked-server refs)
    // are counted for the preview's opt-in checkbox but EXCLUDED from the
    // rewrite unless the user opted in. A body whose only matches are ambiguous
    // (and not opted-in) produces no edit at all.
    const deduped = dedupeByRange(group.references, confidenceByRef);
    ambiguousMatchCount += deduped.filter(
      (r) => (confidenceByRef.get(r) ?? 'exact') === 'ambiguous',
    ).length;
    const includedRefs = deduped.filter((r) => {
      const c = confidenceByRef.get(r) ?? 'exact';
      return c !== 'ambiguous' || includeAmbiguous;
    });
    if (includedRefs.length === 0) continue;
    const isProcedural =
      group.objectKind === 'procedure' || group.objectKind === 'trigger';
    // A failed fetch (`fetchRows` throws) degrades to `body-missing` for this
    // object rather than rejecting the whole plan.
    const body = await (isProcedural ? fetchProceduralBody : fetchBody)({
      connectionId,
      database,
      engineId,
      objectKind: group.objectKind,
      objectName: group.objectName,
    }).catch(() => null);
    if (body == null) {
      unrewritable.push({
        objectKind: group.objectKind,
        objectName: group.objectName,
        reason: 'body-missing',
      });
      continue;
    }
    if (!emitter) {
      unrewritable.push({
        objectKind: group.objectKind,
        objectName: group.objectName,
        reason: 'no-emitter',
      });
      continue;
    }

    // Narrow each surviving reference to the TERMINAL identifier component and
    // render the new name in that component's own delimiter style — the index
    // records whole `<alias>.<column>` chains, so replacing the recorded range
    // wholesale dropped the qualifier (`u.id` → a bare, ambiguous `user_id`)
    // and the delimiters (`` `u`.`email` `` → a bare `email_address`). What goes
    // INSIDE those delimiters is `catalogName`, not the typed name: delimiters
    // suppress folding, so `u."ID"` → `u."user_id"` would name a column Oracle's
    // unquoted `RENAME COLUMN … TO user_id` never created (it made `USER_ID`).
    //
    // Narrowing runs AFTER `dedupeByRange`, not before: the duplicate-recording
    // signal lives in the RAW ranges (the MySQL case is a nested pair —
    // `` `u`.`email` AS `email` `` [24,46) vs `` `u`.`email` `` [24,35) —
    // which is only visible before narrowing), and narrowing cannot introduce a
    // new overlap because each narrowed range is CONTAINED in the range it came
    // from, so disjoint survivors stay disjoint and emitSource's
    // overlapping-edits guard stays satisfied.
    const lineIndex = buildLineIndex(body);
    const rationale = `rename ${fromName} → ${destination.name}`;
    const plannedEdits: PlannedByteEdit[] = includedRefs.map((ref) =>
      planTerminalTokenEdit({
        body,
        lineIndex,
        range: ref.range,
        fromName,
        newName: destination.name,
        catalogName: destination.catalogName,
        engine: sqlEngine,
        rationale,
      }),
    );
    const editConfidence = weakestConfidence(includedRefs, confidenceByRef);
    const newBody = emitSource({ plannedEdits }, body);
    // The SQLite emitter throws NotSupportedError for check-constraint /
    // default-constraint body rewrites (table rebuild not supported in v1),
    // and also for unknown object kinds. Catch any throw from EITHER the
    // forward or undo emit call and degrade to the unrewritable list rather
    // than failing the entire preview.
    //
    // Both emit calls are computed BEFORE any push so that a throw from either
    // leaves zero partial state: a group can never land in both `objectEdits`
    // and `unrewritable` simultaneously.
    try {
      const alterSql = emitter.emitObjectBodyAlter({
        objectKind: group.objectKind,
        objectName: group.objectName,
        newBody,
      });
      const undoSql = emitter.emitObjectBodyAlter({
        objectKind: group.objectKind,
        objectName: group.objectName,
        newBody: body,
      });

      objectEdits.push({
        objectKind: group.objectKind,
        objectName: group.objectName,
        originalBody: body,
        newBody,
        alter: { sql: alterSql, purpose: 'object-body-rewrite', objectName: group.objectName },
        confidence: editConfidence,
      });
      undoBody.push({
        sql: undoSql,
        purpose: 'object-body-rewrite',
        objectName: group.objectName,
      });
    } catch {
      unrewritable.push({
        objectKind: group.objectKind,
        objectName: group.objectName,
        reason: 'unsupported-op',
      });
      continue;
    }
  }

  return { objectEdits, unrewritable, undoBody, ambiguousMatchCount };
}
